import React, { useEffect } from 'react';
import { FeatureBar } from './FeatureBar';
import { QuickReplyBar } from './QuickReplyBar';
import { TableInteractionForm } from './TableInteractionForm';
import type { TableInteractionData } from '../utils/extractInteractiveTable';
import type { AiDifyCapabilityFeatures, PatternFeatureKey } from '../types/aiDifyCapability';
import type { SkillOption } from '../types/skillAsset';

export interface BottomInputAreaProps {
  /**
   * 受控输入值（由父组件维护）
   * - 不传则默认空字符串，保证 input 始终受控，避免控制台告警
   */
  readonly value?: string;
  /** ✨ 输入框上方的附加内容（如代码选区标签） */
  readonly topAddon?: React.ReactNode;

  readonly onChangeInput?: (value: string) => void;
  readonly onClickMic?: () => void;
  readonly onClickSend?: () => void;

  readonly onClickAction1?: () => void;
  readonly onClickAction2?: () => void;
  readonly onClickAction3?: () => void;
  readonly onClickAction4?: () => void;
  readonly onClickDots?: () => void;

  // ===== 新增：特性栏相关 =====
  /** 当前能力的特性配置 */
  readonly capabilityFeatures?: AiDifyCapabilityFeatures;
  /** 技能选项列表 */
  readonly skillOptions?: readonly SkillOption[];
  /** 技能选项加载中 */
  readonly skillsLoading?: boolean;
  /** 已选中的技能 ID */
  readonly selectedSkillIds?: readonly string[];
  /** 切换技能选中状态 */
  readonly onToggleSkill?: (id: string) => void;
  /** 当前选中的模式特性键 */
  readonly selectedPattern?: PatternFeatureKey | null;
  /** 切换模式 */
  readonly onSelectPattern?: (pattern: PatternFeatureKey | null) => void;
  /** 输入框占位符 */
  readonly placeholder?: string;
  /** 是否禁用发送（AI 回复中） */
  readonly disabled?: boolean;

  // ===== 新增：快捷回复 =====
  /** 从 AI 回复中提取的快捷回复列表 */
  readonly quickReplies?: readonly string[];
  /** 点击快捷回复按钮 */
  readonly onQuickReply?: (text: string) => void;

  // ===== 新增：表格交互表单 =====
  /** 从 AI 回复中解析出的可交互表格数据 */
  readonly tableInteraction?: TableInteractionData | null;
  /** 表格表单提交回调（接收组装好的消息文本） */
  readonly onTableSubmit?: (message: string) => void;
  /** 用于重置表格表单状态的唯一标识（通常为最后一条消息 ID） */
  readonly tableInteractionKey?: string;
}

export const BottomInputArea: React.FC<BottomInputAreaProps> = ({
  value,
  topAddon,
  onChangeInput,
  onClickMic,
  onClickSend,
  capabilityFeatures,
  skillOptions,
  skillsLoading,
  selectedSkillIds,
  onToggleSkill,
  selectedPattern,
  onSelectPattern,
  placeholder,
  disabled = false,
  quickReplies,
  onQuickReply,
  tableInteraction,
  onTableSubmit,
  tableInteractionKey,
}) => {
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);

  /** textarea 最大允许高度（px），超过后内部滚动 */
  const TEXTAREA_MAX_HEIGHT = 150;

  /** ★ 自动调整高度：只控制 textarea 自身 */
  const adjustHeight = React.useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    // 先重置为 auto，让浏览器计算真实 scrollHeight
    textarea.style.height = 'auto';

    const desired = textarea.scrollHeight;

    if (desired > TEXTAREA_MAX_HEIGHT) {
      textarea.style.height = `${TEXTAREA_MAX_HEIGHT}px`;
      textarea.style.overflowY = 'auto';
    } else {
      textarea.style.height = `${desired}px`;
      textarea.style.overflowY = 'hidden';
    }
  }, []);

  const handleInputChange = React.useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      onChangeInput?.(e.target.value);
    },
    [onChangeInput]
  );

  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      // Enter 发送；Shift+Enter 换行
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        onClickSend?.();
      }
    },
    [onClickSend]
  );

  // ★ value 变化时（包括外部清空）自动调整高度
  useEffect(() => {
    adjustHeight();
  }, [value, adjustHeight]);

  return (
    <div className="input-area-wrapper">
      {/* 表格交互表单：有可交互表格时显示，key 变化时自动重置内部状态 */}
      {tableInteraction && onTableSubmit && (
        <TableInteractionForm
          key={tableInteractionKey}
          data={tableInteraction}
          disabled={disabled}
          onSubmit={onTableSubmit}
        />
      )}

      {/* 快捷回复按钮栏：仅在有快捷回复时显示 */}
      {quickReplies && quickReplies.length > 0 && onQuickReply && (
        <QuickReplyBar
          replies={quickReplies}
          onQuickReply={onQuickReply}
          disabled={disabled}
        />
      )}

      {/* ✨ 顶部附加区域（代码选区标签等） */}
      {topAddon && (
        <div className="input-area-top-addon">
          {topAddon}
        </div>
      )}
      {/* <div className="top-status-bar">
        <span>
          <i className="fa-solid fa-wand-magic" /> 升级专业版解锁更多功能
        </span>
        <span>
          <i className="fa-solid fa-microchip" /> 由智能助手 v2.6 驱动
        </span>
      </div> */}

      <div className="input-row">
        {/* <i className="fa-solid fa-plus plus-icon" /> */}
        <textarea
          ref={textareaRef}
          className="input-box"
          placeholder={placeholder ?? "随便问点什么..."} // 优先用 prop
          value={value ?? ''}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          rows={1}
        />
        {/* <button className="mic-btn" onClick={onClickMic} type="button">
          <i className="fa-solid fa-microphone" />
        </button> */}
        <button
          className={`send-btn${disabled ? ' send-btn--disabled' : ''}`}
          onClick={onClickSend}
          type="button"
          disabled={disabled}
          title={disabled ? '等待 AI 回复中...' : '发送'}
        >
          <i className={disabled ? 'fa-solid fa-circle-notch fa-spin' : 'fa-solid fa-paper-plane'} />
        </button>
      </div>

      {/* ===== 新增：特性标签栏 ===== */}
      <FeatureBar
        features={capabilityFeatures}
        skillOptions={skillOptions}
        skillsLoading={skillsLoading}
        selectedSkillIds={selectedSkillIds}
        onToggleSkill={onToggleSkill}
        selectedPattern={selectedPattern}
        onSelectPattern={onSelectPattern}
      />
    </div>
  );
};